import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Activity, Loader2, Printer, FileText, MapPin, DollarSign, Search, Plus, ExternalLink, ShieldCheck, Package } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShippingActivityPage() {
  const { token } = useAuth();
  const auth = { Authorization: `Bearer ${token}` };
  const [summary, setSummary] = useState(null);
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trackQuery, setTrackQuery] = useState('');
  const [trackResult, setTrackResult] = useState(null);
  const [tracking, setTracking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        fetch(`${API}/activity/summary`, { headers: auth }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/activity/labels?limit=50`, { headers: auth }).then(r => r.ok ? r.json() : { labels: [] }),
      ]);
      setSummary(s); setLabels(l.labels || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openInTab = (path) => {
    const w = window.open('about:blank');
    fetch(`${API}${path}`, { headers: auth })
      .then(r => r.text())
      .then(html => { w.document.open(); w.document.write(html); w.document.close(); })
      .catch(() => { toast.error('Failed to open'); w.close(); });
  };

  // The carrier's label, with its barcode. "Print" is SelleSend's own summary
  // page, not the label that goes on the box.
  const openCarrierLabel = async (labelId) => {
    const w = window.open('about:blank');
    try {
      const r = await fetch(`${API}/shipping/labels/${labelId}/download`, { headers: auth });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(typeof d.detail === 'string' ? d.detail : 'The label could not be downloaded.');
      }
      if ((r.headers.get('content-type') || '').includes('application/pdf')) {
        w.location.href = URL.createObjectURL(await r.blob());
      } else {
        w.location.href = (await r.json()).url;
      }
    } catch (e) {
      toast.error(e.message);
      w.close();
    }
  };

  // El numero llega como argumento: el boton Track de cada etiqueta hacia
  // setTrackQuery y a los 50 ms leia el estado viejo, asi que rastreaba el
  // numero anterior (revision del 26-oct, E.7).
  const runTrack = async (numero = trackQuery) => {
    if (!numero) return;
    setTracking(true);
    try {
      const r = await fetch(`${API}/activity/track/${encodeURIComponent(numero)}`, { headers: auth });
      if (!r.ok) { toast.error('Not found'); return; }
      setTrackResult(await r.json());
    } finally { setTracking(false); }
  };

  const K = ({ label, value, color = 'slate', 'data-testid': tid }) => (
    <Card data-testid={tid}>
      <CardContent className="p-4">
        <div className="text-xs text-slate-500">{label}</div>
        <div className={`text-2xl font-bold tabular-nums mt-1 text-${color}-600`}>{value}</div>
      </CardContent>
    </Card>
  );

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="activity-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Activity className="w-6 h-6 text-indigo-500" /> Shipping Activity
          </h1>
          <p className="text-slate-500 mt-1">Get and produce labels · print · certify insurance · retrieve bills · track shipments.</p>
        </div>
        <Button asChild data-testid="new-label-btn">
          <Link to="/dashboard/rate-shop"><Plus className="w-4 h-4 mr-1" /> New label</Link>
        </Button>
      </div>

      {loading ? (
        <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid sm:grid-cols-4 gap-4">
            <K label="Labels today"    value={summary?.labels_today ?? 0}         data-testid="kpi-today" />
            <K label="In transit"      value={summary?.in_transit ?? 0}           data-testid="kpi-transit" color="blue" />
            <K label="Insured 30d"     value={`$${(summary?.insured_value_30d ?? 0).toLocaleString()}`} data-testid="kpi-insured" color="emerald" />
            <K label="Pending bill"    value={`$${(summary?.pending_bill ?? 0).toLocaleString(undefined,{minimumFractionDigits:2})}`} data-testid="kpi-bill" color="amber" />
          </div>

          {/* Quick actions */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Quick actions</CardTitle>
              <CardDescription>All on-demand · no wait, no scheduled jobs.</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => openInTab('/activity/bill/on-demand')} data-testid="bill-on-demand-btn">
                <DollarSign className="w-4 h-4 mr-1" /> Retrieve bill on demand
              </Button>
              <Button size="sm" variant="outline" asChild>
                <a href={`${API}/programs/rate-card.html`} target="_blank" rel="noreferrer"><FileText className="w-4 h-4 mr-1" /> Master rate card <ExternalLink className="w-3 h-3 ml-1" /></a>
              </Button>
              <Button size="sm" variant="outline" asChild>
                <Link to="/dashboard/rate-shop"><Plus className="w-4 h-4 mr-1" /> Produce a label</Link>
              </Button>
              <Button size="sm" variant="outline" asChild>
                <Link to="/dashboard/claims"><ShieldCheck className="w-4 h-4 mr-1" /> Open a claim</Link>
              </Button>
            </CardContent>
          </Card>

          {/* Track */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><MapPin className="w-4 h-4" /> Track a shipment</CardTitle></CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Input placeholder="Tracking number" value={trackQuery} onChange={e => setTrackQuery(e.target.value)} data-testid="track-input" />
                <Button onClick={() => runTrack()} disabled={tracking} data-testid="track-btn">
                  {tracking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
              {trackResult && (
                <div className="mt-3 p-3 border rounded-md text-sm bg-slate-50 dark:bg-slate-900" data-testid="track-result">
                  <div><b>{trackResult.tracking_number}</b> · {trackResult.carrier} · {trackResult.status}</div>
                  {trackResult.insurance_verification_number && (
                    <div className="text-xs text-emerald-600 mt-1 font-mono">SLT {trackResult.insurance_verification_number}</div>
                  )}
                  {trackResult.events?.length ? (
                    <ul className="mt-2 space-y-1 text-xs">
                      {trackResult.events.map((e, i) => (
                        <li key={i} className="flex gap-2"><span className="text-slate-400 w-40 shrink-0">{e.timestamp || e.time}</span><span>{e.description || e.status}</span></li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-xs text-slate-500 mt-1">No upstream events yet.</div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Labels list */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Recent labels</CardTitle>
              <CardDescription>Click a row to print, certify, or track.</CardDescription>
            </CardHeader>
            <CardContent>
              {labels.length === 0 ? (
                <p className="text-sm text-slate-500 py-6 text-center">No labels yet. <Link to="/dashboard/rate-shop" className="text-indigo-600 hover:underline">Produce your first one →</Link></p>
              ) : (
                <div className="divide-y">
                  {labels.map(l => (
                    <div key={l.id} className="py-3 flex flex-wrap items-center gap-3" data-testid={`label-row-${l.id.slice(0,8)}`}>
                      <div className="flex-1 min-w-[240px]">
                        <div className="font-mono text-sm break-all">{l.tracking_number}</div>
                        <div className="text-xs text-slate-500">
                          {l.gateway} · {new Date(l.created_at).toLocaleString()}
                          {l.insurance_verification_number && (
                            <span className="ml-2 text-emerald-600 font-mono">SLT {l.insurance_verification_number.slice(0, 20)}</span>
                          )}
                          {l.program_fee && (
                            <span className="ml-2 text-indigo-600">· {l.program_fee.code} ${Number(l.program_fee.fee).toFixed(2)}</span>
                          )}
                        </div>
                      </div>
                      {/* En el celular los cuatro botones no entran en una fila (390 px en 375, 29-oct). */}
                      <div className="flex flex-wrap gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openCarrierLabel(l.id)} data-testid={`label-${l.id.slice(0,8)}`}>
                          <Package className="w-3.5 h-3.5 mr-1" /> Label
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openInTab(`/activity/label/${l.id}/print`)} data-testid={`print-${l.id.slice(0,8)}`}>
                          <Printer className="w-3.5 h-3.5 mr-1" /> Print
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openInTab(`/activity/label/${l.id}/certificate`)} disabled={!l.insurance_verification_number} data-testid={`cert-${l.id.slice(0,8)}`}>
                          <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Certify
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setTrackQuery(l.tracking_number); runTrack(l.tracking_number); }}>
                          <MapPin className="w-3.5 h-3.5 mr-1" /> Track
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
